/**
 * Admin Portal — partner discount TERMS (Codes PR-2, 2026-09-28): the words and checks shared by
 * the Store access page (discount offers per store product) and the Partners tag editor.
 *
 * Owner requirement: a monthly discount lasts a term of N months ("X% off for your first N
 * months"); an annual discount is always the first year. Offered terms: 1, 3, 6 and 12 months,
 * each made live in the stores only when a tag needs it. The stores fix an offer's length when it
 * is created, so every (percentage, length) pair is its own store offer — and on the App Store its
 * own one of a product's 10 offer-code slots.
 *
 * Pure: no fetch, no React. The server (services/discount_offers.py,
 * services/store_offer_buckets.py) is the authority; these only word things and warn early.
 * Web-only (the admin SPA).
 */

export type Period = 'monthly' | 'annual';
export type StoreKey = 'app_store' | 'google_play';

/** The owner's monthly terms (services/discount_offers.MONTHLY_TERM_CHOICES — pinned by jest). */
export const MONTHLY_TERM_CHOICES = [1, 3, 6, 12] as const;
/** An annual discount always lasts the first year. */
export const ANNUAL_TERM_MONTHS = 12;
/** A monthly discount's length before terms existed (and the menu's longest). */
export const DEFAULT_MONTHLY_TERM = 12;

export const STORE_NAMES: Record<StoreKey, string> = { app_store: 'App Store', google_play: 'Google Play' };

/** "first year" · "first month" · "first 3 months". */
export function termLabel(period: Period, months: number | null | undefined): string {
  if (period === 'annual') return 'first year';
  const n = Number(months) || DEFAULT_MONTHLY_TERM;
  return n === 1 ? 'first month' : `first ${n} months`;
}

/** The choice text in a term picker: "3 months", "1 month". */
export function termChoiceLabel(months: number): string {
  return months === 1 ? '1 month' : `${months} months`;
}

/** "15% off monthly · first 3 months" / "30% off annual · first year". */
export function discountLabel(period: Period, pct: number, months: number | null | undefined): string {
  return `${pct}% off ${period} · ${termLabel(period, months)}`;
}

/** A product id without the App Store prefix, for lists: "full.monthly". */
export function shortProduct(productId: string): string {
  return productId.replace(/^com\.coachcedar\.app\./, '');
}

export interface DiscountOffer {
  pct: number;
  months: number;
  status: 'active' | 'retired' | string;
  level_status: 'active' | 'retired' | string;
  tags_using?: number;
  grants_holding?: number;
}

/**
 * The terms a monthly picker may offer for level `pct`: every ACTIVE pair of an active level, plus
 * the term the tag already carries even if retired since (the server keeps it, never sells it
 * anew). Before the menu has loaded, the owner's terms.
 */
export function monthlyTermChoices(offers: DiscountOffer[] | undefined, pct: number | null,
  current?: { pct: number | null; months: number | null }): number[] {
  if (pct == null) return [];
  const out = (offers && offers.length)
    ? offers.filter((o) => o.pct === pct && o.status === 'active' && o.level_status === 'active').map((o) => o.months)
    : [...MONTHLY_TERM_CHOICES];
  if (current && current.pct === pct && current.months != null && !out.includes(current.months)) out.push(current.months);
  return out.sort((a, b) => a - b);
}

/**
 * The length a create form picks when its monthly LEVEL changes (review of #4217, N3): 12 months
 * when that pair is on sale, else the longest length that is — never a stale choice from the level
 * picked before.
 */
export function defaultMonthlyTerm(offers: DiscountOffer[] | undefined, pct: number | null): number {
  const choices = monthlyTermChoices(offers, pct);
  if (!choices.length || choices.includes(DEFAULT_MONTHLY_TERM)) return DEFAULT_MONTHLY_TERM;
  return choices[choices.length - 1];
}

/** Why a create form's monthly length can't be sent (it is not an active pair for the level), or null. */
export function monthlyTermError(offers: DiscountOffer[] | undefined, pct: number | null, months: number): string | null {
  if (pct == null) return null;
  return monthlyTermChoices(offers, pct).includes(months)
    ? null
    : `Monthly discount: ${pct}% off for ${termChoiceLabel(months)} is not on the menu — choose how long it lasts.`;
}

// ── store coverage: can the discount a tag promises be bought? ────────────────────────────

export interface MissingProduct { store: StoreKey; product_id: string }

export interface PeriodCoverage {
  pct: number;
  months: number;
  label: string;
  state: 'ok' | 'partial' | 'none';
  missing: MissingProduct[];
}

export interface TagCoverage {
  tag_id: string;
  tag: string;
  name: string;
  status: string;
  partner_id: string;
  partner_code: string;
  partner_name: string;
  monthly: PeriodCoverage | null;
  annual: PeriodCoverage | null;
  buyable_everywhere: boolean;
}

export interface LiveStoreOffer { pct: number; months: number; store: StoreKey; product_id: string }

export interface DiscountCoverage {
  tags: TagCoverage[];
  unbuyable: number;
  live_offers: LiveStoreOffer[];
  products: Record<StoreKey, Array<{ product_id: string; period: string }>>;
}

/** Every product of `period` (both stores) with NO live store offer for this percentage and
 * length — the server's own rule (services/discount_offers.missing_products). */
export function comboMissing(cov: DiscountCoverage | undefined, period: Period, pct: number,
  months: number | null | undefined): MissingProduct[] {
  if (!cov) return [];
  const term = period === 'annual' ? ANNUAL_TERM_MONTHS : (Number(months) || DEFAULT_MONTHLY_TERM);
  const live = new Set(cov.live_offers.map((o) => `${o.pct}|${o.months}|${o.store}|${o.product_id}`));
  const out: MissingProduct[] = [];
  for (const store of ['app_store', 'google_play'] as StoreKey[]) {
    for (const p of cov.products?.[store] ?? []) {
      if (p.period === period && !live.has(`${pct}|${term}|${store}|${p.product_id}`)) {
        out.push({ store, product_id: p.product_id });
      }
    }
  }
  return out;
}

/**
 * The warning for one period of a discount that some store product cannot sell, or null when it
 * is live everywhere. The redeem card promises the discount; without a live store offer the
 * person meets the normal paywall instead — so nobody should promise it yet.
 */
export function coverageWarning(period: Period, pct: number, months: number | null | undefined,
  missing: MissingProduct[]): string | null {
  if (!missing.length) return null;
  const byStore = (['app_store', 'google_play'] as StoreKey[])
    .map((s) => ({ s, ps: missing.filter((m) => m.store === s).map((m) => shortProduct(m.product_id)) }))
    .filter((x) => x.ps.length)
    .map((x) => `${STORE_NAMES[x.s]}: ${x.ps.join(', ')}`);
  return `No live store offer for ${discountLabel(period, pct, months)} on ${byStore.join('; ')}. `
    + 'People who redeem this code will be promised a discount they can’t buy there — '
    + 'create the offer in the store and switch it on under Store access first.';
}

/** The warnings for one tag (0, 1 or 2 lines). */
export function tagCoverageWarnings(t: TagCoverage): string[] {
  const out: string[] = [];
  for (const period of ['monthly', 'annual'] as Period[]) {
    const c = t[period];
    const w = c ? coverageWarning(period, c.pct, c.months, c.missing) : null;
    if (w) out.push(w);
  }
  return out;
}

/** The summary line for a list of tags, or null when every live discount can be bought. */
export function coverageSummary(cov: DiscountCoverage | undefined): string | null {
  if (!cov || !cov.unbuyable) return null;
  const n = cov.unbuyable;
  return `${n} live partner link${n === 1 ? '' : 's'} promise${n === 1 ? 's' : ''} a discount that some store `
    + 'product has no live offer for — people who redeem the code can’t buy it on those products.';
}

// ── the old-build notice (Codes PR-2 follow-up L-D) ──────────────────────────
// services/discount_offers' OLD-BUILD GATE: an app build below PARTNER_OFFER_TERMS_MIN_BUILD is
// never shown a monthly discount whose term is not 12 months (it would print "for your first
// year" for any length). Admins must know a short term reaches only the newer builds.

/** The one line an admin sees wherever a tag's monthly discount lasts less than 12 months. */
export const SHORT_TERM_NOTICE =
  'Short monthly discounts (1–6 months) only show in app builds that support them; older builds see no monthly discount.';

/** The notice for a monthly discount of `months`, or null (none, or the full 12 months). */
export function shortTermNotice(months: number | null | undefined): string | null {
  return months != null && Number(months) > 0 && Number(months) < DEFAULT_MONTHLY_TERM ? SHORT_TERM_NOTICE : null;
}

/** Whether any live tag in the coverage answer carries a monthly discount shorter than 12 months. */
export function coverageHasShortTerm(cov: DiscountCoverage | undefined): boolean {
  return !!cov && cov.tags.some((t) => t.status !== 'ended' && !!t.monthly && shortTermNotice(t.monthly.months) !== null);
}
